import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import useRole from "../hooks/useRole";
import { crmHref } from "../paths";
import CrmButton from "../ui/CrmButton";
import CrmTooltip from "../ui/CrmTooltip";
import CrmMenu from "../ui/CrmMenu";
import BookSessionModal from "../booking/BookSessionModal";
import LogInteractionPopover from "./LogInteractionPopover";
import SendEmailDialog from "./SendEmailDialog";
import { DeleteClientDialog, EraseClientDialog, MergeDuplicateDialog, UnsubscribeDialog } from "./PrivacyDialogs";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The action row (spec Screen 3): Book session · Log interaction · Send email (disabled
 * with "Client has not consented" unless consented) · ⋮ more (Merge duplicate,
 * Unsubscribe client, Erase client data, Delete client — owner only for all but
 * Unsubscribe).
 */
export default function ClientActions({ client, setClient, onChanged }) {
  const t = useT("crm");
  const { can } = useRole();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const consented = (client.consent?.status || client.consent_status) === "consented";
  const owner = can("mergeEraseDelete");

  const items = [
    ...(owner ? [{ key: "merge", label: t("more.merge"), onSelect: () => setDialog("merge"), testId: CRM.clientMoreItem("merge") }] : []),
    { key: "unsubscribe", label: t("more.unsubscribe"), onSelect: () => setDialog("unsubscribe"), testId: CRM.clientMoreItem("unsubscribe"),
      disabled: (client.consent?.status || client.consent_status) === "unsubscribed" },
    ...(owner ? [
      { key: "erase", label: t("more.erase"), onSelect: () => setDialog("erase"), testId: CRM.clientMoreItem("erase") },
      { key: "delete", label: t("more.delete"), hint: t("more.ownerOnly"), tone: "danger", onSelect: () => setDialog("delete"), testId: CRM.clientMoreItem("delete") },
    ] : []),
  ];

  const sendEmail = (
    <CrmButton variant="outline" disabled={!consented} onClick={() => setDialog("email")} data-testid={CRM.clientSendEmail}>
      {t("actions.sendEmail")}
    </CrmButton>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <CrmButton onClick={() => setDialog("book")} data-testid={CRM.clientBook}>{t("actions.book")}</CrmButton>
      <LogInteractionPopover clientId={client.id} onLogged={onChanged} />
      {consented ? sendEmail : (
        <CrmTooltip content={t("actions.notConsented")} testId={CRM.clientSendEmailTooltip}>{sendEmail}</CrmTooltip>
      )}
      <CrmMenu
        header={t("more.header")}
        items={items}
        trigger={(
          <CrmButton variant="ghost" aria-label={t("more.label")} data-testid={CRM.clientMore} className="px-2">
            <MoreVertical size={18} aria-hidden="true" />
          </CrmButton>
        )}
      />
      {dialog === "book" && <BookSessionModal client={client} onClose={() => setDialog(null)} onBooked={onChanged} />}
      {dialog === "email" && <SendEmailDialog client={client} onClose={() => setDialog(null)} onSent={onChanged} />}
      {dialog === "merge" && <MergeDuplicateDialog client={client} onClose={() => setDialog(null)} onMerged={(c) => { if (c?.id) setClient(c); onChanged(); }} />}
      {dialog === "unsubscribe" && <UnsubscribeDialog client={client} onClose={() => setDialog(null)} onDone={() => onChanged()} />}
      {dialog === "erase" && <EraseClientDialog client={client} onClose={() => setDialog(null)} onErased={onChanged} />}
      {dialog === "delete" && <DeleteClientDialog client={client} onClose={() => setDialog(null)} onDeleted={() => navigate(crmHref("/clients"), { replace: true })} />}
    </div>
  );
}
